'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { Slot } from 'radix-ui';
import { Children, isValidElement } from 'react';
import type { IconSvgElement } from '@/components/ui/icon';
import { Icon } from '@/components/ui/icon';
import { MOTION_HOVER, MOTION_TAP } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------

const variantClasses = {
  primary: 'bg-button-primary-bg text-button-primary-text hover:bg-button-primary-bg-hover focus-visible:ring-button-primary-ring/30',
  secondary: 'bg-background-tertiary text-foreground-primary hover:bg-background-tertiary/80 focus-visible:ring-border-primary/30',
  tertiary: 'bg-transparent text-foreground-secondary hover:bg-background-tertiary focus-visible:ring-border-primary/30',
  outline: 'border bg-transparent text-foreground-secondary hover:bg-background-tertiary focus-visible:ring-border-primary/30',
  destructive: 'bg-button-destructive-bg text-button-destructive-text hover:bg-button-destructive-bg-hover focus-visible:ring-button-destructive-ring/20'
} as const;
const labelSizeClasses = {
  default: 'h-9 gap-1.5 rounded-control text-body',
  sm: 'h-8 gap-1.5 rounded-control text-body',
  xs: 'h-6 gap-1 rounded-control text-caption',
  lg: 'h-10 gap-2 rounded-[0.875rem] text-body',
  xl: 'h-12 gap-2 rounded-[1rem] text-body-lg'
} as const;
const paddingLeft = {
  default: {
    full: 'pl-4',
    reduced: 'pl-3'
  },
  sm: {
    full: 'pl-3',
    reduced: 'pl-2.5'
  },
  xs: {
    full: 'pl-2',
    reduced: 'pl-1.5'
  },
  lg: {
    full: 'pl-6',
    reduced: 'pl-4'
  },
  xl: {
    full: 'pl-8',
    reduced: 'pl-6'
  }
} as const;
const paddingRight = {
  default: {
    full: 'pr-4',
    reduced: 'pr-3'
  },
  sm: {
    full: 'pr-3',
    reduced: 'pr-2.5'
  },
  xs: {
    full: 'pr-2',
    reduced: 'pr-1.5'
  },
  lg: {
    full: 'pr-6',
    reduced: 'pr-4'
  },
  xl: {
    full: 'pr-8',
    reduced: 'pr-6'
  }
} as const;
const iconSizeClasses = {
  default: 'size-icon-md',
  sm: 'size-icon-md',
  xs: 'size-icon-sm',
  lg: 'size-icon-lg',
  xl: 'size-icon-lg'
} as const;
const iconOnlySizeClasses = {
  default: 'aspect-square size-9 rounded-control',
  sm: 'aspect-square size-8 rounded-control',
  xs: 'aspect-square size-6 rounded-control',
  lg: 'aspect-square size-10 rounded-[0.875rem]',
  xl: 'aspect-square size-12 rounded-[1rem]'
} as const;
export type ButtonVariant = keyof typeof variantClasses;
export type ButtonSize = keyof typeof labelSizeClasses;
function renderableChildren(children: React.ReactNode): Array<React.ReactNode> {
  return Children.toArray(children).filter(child => child != null && typeof child !== 'boolean');
}
function isSingleIconElement(child: React.ReactNode): boolean {
  return isValidElement(child) && child.type === Icon;
}

// ---------------------------------------------------------------------------
// Motion config
// ---------------------------------------------------------------------------

// Hover lift is now a CSS shadow transition, not a transform — scaling the
// button also scales the text glyphs, which causes a sub-pixel re-rasterise on
// every hover. Tap remains a Motion transform: it's brief and the press-down
// feel is worth the trade-off.
const TAP_SCALE = {
  scale: 0.97,
  transition: MOTION_TAP
} as const;
const HOVER_SHADOW_CLASS = 'hover:shadow-elevation-inset' as const;
const BASE_CLASSES = 'touch-target select-none appearance-none inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap outline-none cursor-pointer focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 transition-shadow duration-fast ease-default motion-reduce:transition-none' as const;

// Private CSS properties give Figma Dev Mode exact, code-owned references
// without promoting component geometry into the public Foundations scale.
const WEB_BUTTON_METRIC_CLASSES: string = ['[--web-button-content-color:currentColor]', '[--web-button-height-xs:24px]', '[--web-button-gap-xs:4px]', '[--web-button-padding-full-xs:8px]', '[--web-button-padding-reduced-xs:6px]', '[--web-button-radius-xs:12px]', '[--web-button-icon-xs:14px]', '[--web-button-height-sm:32px]', '[--web-button-gap-sm:6px]', '[--web-button-padding-full-sm:12px]', '[--web-button-padding-reduced-sm:10px]', '[--web-button-radius-sm:12px]', '[--web-button-icon-sm:16px]', '[--web-button-height-default:36px]', '[--web-button-gap-default:6px]', '[--web-button-padding-full-default:16px]', '[--web-button-padding-reduced-default:12px]', '[--web-button-radius-default:12px]', '[--web-button-icon-default:16px]', '[--web-button-height-lg:40px]', '[--web-button-gap-lg:8px]', '[--web-button-padding-full-lg:24px]', '[--web-button-padding-reduced-lg:16px]', '[--web-button-radius-lg:14px]', '[--web-button-icon-lg:20px]', '[--web-button-height-xl:48px]', '[--web-button-gap-xl:8px]', '[--web-button-padding-full-xl:32px]', '[--web-button-padding-reduced-xl:24px]', '[--web-button-radius-xl:16px]', '[--web-button-icon-xl:20px]', '[--web-button-focus-ring-width:3px]', '[--web-button-opacity-disabled:0.5]', '[--web-button-opacity-focus-ring:0.3]', '[--web-button-opacity-destructive-focus-ring:0.2]', '[--web-button-opacity-secondary-hover:0.8]'].join(' ');

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AnimatedButtonProps {
  /** Visual style. @default 'primary' */
  variant?: ButtonVariant | undefined;
  /** Size derived from shadcn button scale. @default 'default' */
  size?: ButtonSize | undefined;
  /** Icon rendered before the label. */
  prefix?: IconSvgElement | undefined;
  /** Extra className merged onto the prefix Icon — use for `animate-spin` on
   *  pending-state spinners or other per-callsite icon styling. */
  prefixClassName?: string | undefined;
  /** Icon rendered after the label. */
  suffix?: IconSvgElement | undefined;
  /**
   * Label content. Icon-only chrome (square): use `prefix`/`suffix` without
   * children, or pass a single `<Icon />` child (no prefix/suffix).
   */
  children?: React.ReactNode | undefined;
  className?: string | undefined;
  disabled?: boolean | undefined;
  /** When true, skips the hover shadow lift. Tap feedback is unchanged. */
  disableHoverScale?: boolean | undefined;
  /** When true, skips tap scale. */
  disableTapScale?: boolean | undefined;
  /** @default 'button' */
  type?: 'button' | 'submit' | 'reset' | undefined;
  /** Associate the button with a `<form>` by id when it is rendered outside the form
   *  element. Used by modal footers whose submit button lives in the SilkSheet `footer`
   *  slot but submits the form in the body. Only valid alongside `type="submit"`. */
  form?: string | undefined;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onPointerEnter?: React.PointerEventHandler<HTMLButtonElement> | undefined;
  onPointerLeave?: React.PointerEventHandler<HTMLButtonElement> | undefined;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onFocus?: React.FocusEventHandler<HTMLButtonElement> | undefined;
  onBlur?: React.FocusEventHandler<HTMLButtonElement> | undefined;
  /**
   * Render via Radix `Slot.Root` so the chrome class set is forwarded onto a
   * single child element (e.g. `<Link>`, `<a>`, `<DialogClose>`). Motion is
   * suppressed in this branch — the child element manages its own behaviour
   * and Radix Slot cannot drive motion props on arbitrary children.
   * @default false
   */
  asChild?: boolean | undefined;
  'aria-label'?: string | undefined;
  'aria-describedby'?: string | undefined;
  'aria-expanded'?: boolean | undefined;
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'] | undefined;
  'aria-pressed'?: boolean | undefined;
  'aria-controls'?: string | undefined;
  'aria-current'?: React.AriaAttributes['aria-current'] | undefined;
  'data-testid'?: string | undefined;
  'data-simulation-cta'?: string | undefined;
  id?: string | undefined;
  name?: string | undefined;
  tabIndex?: number | undefined;
  title?: string | undefined;
  role?: string | undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Morfi's canonical button wrapper. */
export function AnimatedButton(t0) {
  const $ = _c(77);
  let aria;
  let children;
  let className;
  let disableHoverScale;
  let disableTapScale;
  let disabled;
  let form;
  let onBlur;
  let onClick;
  let onFocus;
  let onMouseEnter;
  let onMouseLeave;
  let onPointerEnter;
  let onPointerLeave;
  let prefix;
  let prefixClassName;
  let suffix;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      variant: t1,
      size: t2,
      prefix,
      prefixClassName,
      suffix,
      children,
      className,
      disabled,
      disableHoverScale,
      disableTapScale,
      type: t3,
      form,
      onClick,
      onPointerEnter,
      onPointerLeave,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      asChild: t4,
      ...aria
    } = t0);
    $[0] = t0;
    $[1] = aria;
    $[2] = children;
    $[3] = className;
    $[4] = disableHoverScale;
    $[5] = disableTapScale;
    $[6] = disabled;
    $[7] = form;
    $[8] = onBlur;
    $[9] = onClick;
    $[10] = onFocus;
    $[11] = onMouseEnter;
    $[12] = onMouseLeave;
    $[13] = onPointerEnter;
    $[14] = onPointerLeave;
    $[15] = prefix;
    $[16] = prefixClassName;
    $[17] = suffix;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
  } else {
    aria = $[1];
    children = $[2];
    className = $[3];
    disableHoverScale = $[4];
    disableTapScale = $[5];
    disabled = $[6];
    form = $[7];
    onBlur = $[8];
    onClick = $[9];
    onFocus = $[10];
    onMouseEnter = $[11];
    onMouseLeave = $[12];
    onPointerEnter = $[13];
    onPointerLeave = $[14];
    prefix = $[15];
    prefixClassName = $[16];
    suffix = $[17];
    t1 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
  }
  const variant = t1 === undefined ? "primary" : t1;
  const size = t2 === undefined ? "default" : t2;
  const type = t3 === undefined ? "button" : t3;
  const asChild = t4 === undefined ? false : t4;
  const prefersReduced = useReducedMotion();
  let t5;
  if ($[22] !== onBlur || $[23] !== onClick || $[24] !== onFocus || $[25] !== onMouseEnter || $[26] !== onMouseLeave || $[27] !== onPointerEnter || $[28] !== onPointerLeave) {
    t5 = {
      onClick,
      onPointerEnter,
      onPointerLeave,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur
    };
    $[22] = onBlur;
    $[23] = onClick;
    $[24] = onFocus;
    $[25] = onMouseEnter;
    $[26] = onMouseLeave;
    $[27] = onPointerEnter;
    $[28] = onPointerLeave;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  const handlers = t5;
  const hasPrefix = prefix !== undefined;
  const hasSuffix = suffix !== undefined;
  let t6;
  let usesGlyphIconOnly;
  if ($[30] !== children || $[31] !== hasPrefix || $[32] !== hasSuffix) {
    const nonWhitespaceChildren = renderableChildren(children);
    usesGlyphIconOnly = nonWhitespaceChildren.length === 0 && (hasPrefix || hasSuffix);
    t6 = nonWhitespaceChildren.length === 1 && isSingleIconElement(nonWhitespaceChildren[0]) && !hasPrefix && !hasSuffix;
    $[30] = children;
    $[31] = hasPrefix;
    $[32] = hasSuffix;
    $[33] = t6;
    $[34] = usesGlyphIconOnly;
  } else {
    t6 = $[33];
    usesGlyphIconOnly = $[34];
  }
  const usesSingleIconChild = t6;
  const isIconOnly = usesGlyphIconOnly || usesSingleIconChild;
  const iconClass = iconSizeClasses[size];
  let t7;
  if ($[35] !== className || $[36] !== disableHoverScale || $[37] !== disabled || $[38] !== hasPrefix || $[39] !== hasSuffix || $[40] !== isIconOnly || $[41] !== size || $[42] !== variant) {
    const sizeClass = isIconOnly ? iconOnlySizeClasses[size] : cn(labelSizeClasses[size], paddingLeft[size][hasPrefix ? "reduced" : "full"], paddingRight[size][hasSuffix ? "reduced" : "full"]);
    t7 = cn(BASE_CLASSES, WEB_BUTTON_METRIC_CLASSES, variantClasses[variant], sizeClass, disableHoverScale !== true && disabled !== true && HOVER_SHADOW_CLASS, className);
    $[35] = className;
    $[36] = disableHoverScale;
    $[37] = disabled;
    $[38] = hasPrefix;
    $[39] = hasSuffix;
    $[40] = isIconOnly;
    $[41] = size;
    $[42] = variant;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  const composedClassName = t7;
  const glyphIconOnly = usesGlyphIconOnly ? prefix ?? suffix : undefined;
  let t8;
  if ($[44] !== children || $[45] !== glyphIconOnly || $[46] !== hasPrefix || $[47] !== hasSuffix || $[48] !== iconClass || $[49] !== prefix || $[50] !== prefixClassName || $[51] !== suffix) {
    t8 = glyphIconOnly !== undefined ? <Icon icon={glyphIconOnly} className={cn(iconClass, "shrink-0")} /> : <>{hasPrefix && <Icon icon={prefix} className={cn(iconClass, "shrink-0", prefixClassName)} />}{children}{hasSuffix && <Icon icon={suffix} className={cn(iconClass, "shrink-0")} />}</>;
    $[44] = children;
    $[45] = glyphIconOnly;
    $[46] = hasPrefix;
    $[47] = hasSuffix;
    $[48] = iconClass;
    $[49] = prefix;
    $[50] = prefixClassName;
    $[51] = suffix;
    $[52] = t8;
  } else {
    t8 = $[52];
  }
  const content = t8;
  if (asChild) {
    let t9;
    if ($[53] !== aria || $[54] !== children || $[55] !== composedClassName) {
      t9 = <Slot.Root className={composedClassName} {...aria}>{children}</Slot.Root>;
      $[53] = aria;
      $[54] = children;
      $[55] = composedClassName;
      $[56] = t9;
    } else {
      t9 = $[56];
    }
    return t9;
  }
  if (prefersReduced) {
    let t9;
    if ($[57] !== aria || $[58] !== composedClassName || $[59] !== content || $[60] !== disabled || $[61] !== form || $[62] !== handlers || $[63] !== type) {
      t9 = <button className={composedClassName} disabled={disabled} type={type} form={form} {...handlers} {...aria}>{content}</button>;
      $[57] = aria;
      $[58] = composedClassName;
      $[59] = content;
      $[60] = disabled;
      $[61] = form;
      $[62] = handlers;
      $[63] = type;
      $[64] = t9;
    } else {
      t9 = $[64];
    }
    return t9;
  }
  let t9;
  if ($[65] !== disableTapScale || $[66] !== disabled) {
    t9 = disabled === true ? {} : {
      ...(disableTapScale === true ? {} : {
        whileTap: TAP_SCALE
      })
    };
    $[65] = disableTapScale;
    $[66] = disabled;
    $[67] = t9;
  } else {
    t9 = $[67];
  }
  const motionProps = t9;
  let t10;
  if ($[68] !== aria || $[69] !== composedClassName || $[70] !== content || $[71] !== disabled || $[72] !== form || $[73] !== handlers || $[74] !== motionProps || $[75] !== type) {
    t10 = <LazyMotion features={domAnimation}><m.button {...motionProps} transition={MOTION_HOVER} className={composedClassName} disabled={disabled} type={type} form={form} {...handlers} {...aria}>{content}</m.button></LazyMotion>;
    $[68] = aria;
    $[69] = composedClassName;
    $[70] = content;
    $[71] = disabled;
    $[72] = form;
    $[73] = handlers;
    $[74] = motionProps;
    $[75] = type;
    $[76] = t10;
  } else {
    t10 = $[76];
  }
  return t10;
}